import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Typography
} from '@material-ui/core';
import { Close, Description, GetApp } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { downloadedSampleFile, getFileNameImportStatus, getSampleFile, importFile, resetGetFileNameStatus, resetImportSucceeded } from 'store/lists/actions';
import { dismissButton, dispatchButton } from 'helpers/error/buttonBuilders';
import { actions as userMessageActions } from 'store/userMessaging';

const useStyles = makeStyles(theme => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  uploadContainer: {
    position: 'relative',
    border: '2px dashed ' + theme.palette.importComponent.borderColor,
    padding: '30px',
    textAlign: 'center !important',
    '&:hover': {
      background: theme.palette.importComponent.hoverColor,
      cursor: 'pointer'
    },
    width: '100%',
    minHeight: '150px'
  },
  hoverOnUpload: {
    background: theme.palette.importComponent.hoverColor,
    cursor: 'pointer'
  },
  uploadContainerSpan: {
    color: theme.palette.importComponent.borderColor
  },
  templateButtonError: {
    paddingTop: '24px',
    color: theme.palette.error.main
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  dropArea: {
    marginTop: '16px',
    minHeight: '150px'
  },
  backdropDiv: {
    pointerEvents: 'none',
    opacity: '0.2',
  },
  overlayDiv: {
    position: 'absolute',
    width: '90%',
    textAlign: 'center',
    marginTop: '15%',
  }
}));

const ImportFileModal = props => {
  const { isOpen, closeImportModal, importCsv } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { listId } = useParams();

  const gettingFileSucceeded = useSelector(store => store.lists.sampleFile.gettingFileSucceeded);
  const gettingFile = useSelector(store => store.lists.sampleFile.gettingFile);
  const isDownloading = useSelector(store => store.lists.sampleFile.isDownloading);
  const isGettingFileNameStatus = useSelector(store => store.lists.fileNameStatus.getting);
  const isFileNameProcessing = useSelector(store => store.lists.fileNameStatus.isFileProcessing);
  const gettingFileNameStatusSucceeded = useSelector(store => store.lists.fileNameStatus.gettingSucceeded);
  const isImportSucceeded = useSelector(store => store.lists.isImportSucceeded);
  const isImportingFile = useSelector(store => store.lists.isImportingFile);
  const sampleFileBlob = useSelector(store => store.lists.sampleFile.sampleFileBlob);

  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!gettingFile) {
      if (gettingFileSucceeded && sampleFileBlob.type === 'text/csv') {
        const url = window.URL.createObjectURL(new Blob([sampleFileBlob]));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'sampleCsvFile.csv';
        link.click();
      }
      dispatch(downloadedSampleFile());
    }
  }, [gettingFileSucceeded]);

  useEffect(() => {
    if (!isGettingFileNameStatus && gettingFileNameStatusSucceeded) {
      if (isFileNameProcessing) {
        dispatch(
          userMessageActions.setMessage({
            title: 'Import Confirm',
            severity: 'warning',
            instructions: `Another file with same name is still being processed. Are you sure you want to import this file?`,
            size: 'xs',
            buttons: [
              dispatchButton('Import', importFile({ listId: listId, file: file })),
              dismissButton('Cancel')
            ]
          })
        );
      } else {
        importCsv(file);
      }
    }
  }, [isGettingFileNameStatus, isFileNameProcessing, gettingFileNameStatusSucceeded]);

  useEffect(() => {
    if (isImportSucceeded) {
      dispatch(resetImportSucceeded());
      closeImportModal();
    }
  }, [isImportSucceeded]);

  const onClickDownloadSampleCsv = () => {
    dispatch(getSampleFile());
  };

  const onImport = () => {
    const fileInput = document.getElementById('file_upload');
    const selectedFiles = [...fileInput.files];
    if (selectedFiles && selectedFiles.length === 1) {
      let selectedFile = selectedFiles[0];
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setShowError(selectedFile.type !== 'text/csv');
    }
  };

  const allowDrop = event => {
    event.preventDefault();
    setDragOver(true);
  };

  const onClickImport = () => {
    dispatch(getFileNameImportStatus({ listId: listId, fileName: file.name, states: ['QUEUED', 'PROCESSING'] }));
  };

  const drop = event => {
    event.preventDefault();
    setFile(event.dataTransfer.files[0]);
    setFileName(event.dataTransfer.files[0].name);
    setDragOver(false);
  };

  const disableImportButton = () => {
    return !file || showError || isGettingFileNameStatus || isImportingFile;
  };

  return (
    <Dialog
      open={isOpen}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="sm">
      <Typography variant="h2">
        <DialogTitle id="form-dialog-title" disableTypography={true}>
          Import File
        </DialogTitle>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={() => {
            dispatch(resetGetFileNameStatus());
            closeImportModal(true)
          }}>
          <Close color="error" />
        </IconButton>
      </Typography>
      <DialogContent style={{ maxHeight: '400px' }}>
        <Card>
          <CardContent>
            {(isGettingFileNameStatus || isImportingFile) &&
              <div className={classes.overlayDiv}>
                <CircularProgress />
              </div>
            }
            <div className={classes.content}>
              <span>
                {showError && (
                  <Typography
                    className={classes.templateButtonError}
                    variant="caption"
                    display="block">
                    *Invalid File format. Please upload a file of csv type.
                  </Typography>
                )}
              </span>
              <Button
                onClick={onClickDownloadSampleCsv}
                startIcon={<GetApp />}
                disabled={isDownloading}
                color="primary"
                variant="outlined">
                Download Sample File
              </Button>
            </div>
            <div className={(isGettingFileNameStatus || isImportingFile) ? classes.backdropDiv : ''}>
              <div>
                <input
                  data-testid="uploadFile"
                  style={{ display: 'none' }}
                  type="file"
                  id="file_upload"
                  accept=".csv"
                  onChange={() => onImport()}
                />
              </div>

              <div
                id="drop-id"
                className={clsx(
                  classes.dropArea,
                  `${dragOver ? classes.hoverOnUpload : ''}`
                )}
                onDrop={event => drop(event)}
                onDragOver={event => allowDrop(event)}
                onDragLeave={() => setDragOver(false)}>
                <label
                  className={classes.uploadContainer}
                  htmlFor="file_upload"
                  component="span">
                  {!file ? (
                    <div className={classes.uploadContainerSpan}>
                      <div>
                        <span>Click</span>
                        <Link
                          style={{ marginLeft: '8px' }}
                          color="secondary"
                          component="span"
                          variant="body1">
                          Upload
                        </Link>
                      </div>
                      <div>or</div>
                      <div>Drag and Drop a CSV file</div>
                    </div>
                  ) : (
                    <div>
                      <Description />
                      <Typography>{fileName}</Typography>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions style={{ marginBottom: '6px' }}>
        <Button
          style={{ marginRight: '16px' }}
          disabled={disableImportButton()}
          onClick={() => onClickImport()}
          color="primary"
          variant="contained">
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportFileModal;
